import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
  UserSuccessOutput,
  UserUserDeactivateActivateInput,
  UserUserIO,
} from '../../generated/fresh-guard-api';
import { queryKeys } from '../query-keys';

export function useUsersDeactivateActivateOptimistic() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      userUserDeactivateActivateInput: UserUserDeactivateActivateInput;
      currentUsername: string;
    }) => {
      const res = await api.userApi.apiUsersDeactivateActivatePut(
        input.userUserDeactivateActivateInput
      );
      return res.data as UserSuccessOutput;
    },
    onMutate: async ({ userUserDeactivateActivateInput, currentUsername }) => {
      const toActivate = userUserDeactivateActivateInput.usersForActivation ?? [];
      const toDeactivate = userUserDeactivateActivateInput.usersForDeactivation ?? [];
      const touched = [...toActivate, ...toDeactivate];

      const now = new Date().toISOString();

      await Promise.all(
        touched
          .filter((username): username is string => username !== null)
          .map((username) => qc.cancelQueries({ queryKey: queryKeys.users.detail(username) }))
      );

      const previousByUsername = new Map<string, UserUserIO | undefined>();

      for (const username of touched) {
        if (username === null) continue;
        const prev = qc.getQueryData<UserUserIO>(queryKeys.users.detail(username));
        previousByUsername.set(username, prev);
        if (!prev) continue;

        const isDeactivating = toDeactivate.includes(username);
        const isActivating = toActivate.includes(username);

        if (username !== null) {
          qc.setQueryData<UserUserIO>(queryKeys.users.detail(username), {
            ...prev,
            ...(isDeactivating
              ? { deactivated: now, deactivatedBy: currentUsername }
              : isActivating
                ? { deactivated: null, deactivatedBy: null }
                : {}),
          });
        }
      }

      return { previousByUsername };
    },
    onError: (_err, variables, context) => {
      if (!context) return;

      const toActivate = variables.userUserDeactivateActivateInput.usersForActivation ?? [];
      const toDeactivate = variables.userUserDeactivateActivateInput.usersForDeactivation ?? [];
      const touched = [...toActivate, ...toDeactivate];

      for (const username of touched) {
        if (username === null) continue;
        const prev = context.previousByUsername.get(username);
        if (prev !== undefined) {
          qc.setQueryData(queryKeys.users.detail(username), prev);
        }
      }
    },
    onSettled: (_data, _err, variables) => {
      const toActivate = variables.userUserDeactivateActivateInput.usersForActivation ?? [];
      const toDeactivate = variables.userUserDeactivateActivateInput.usersForDeactivation ?? [];
      const touched = [...toActivate, ...toDeactivate];

      for (const username of touched) {
        if (username === null) continue;
        void qc.invalidateQueries({
          queryKey: queryKeys.users.detail(username),
        });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
    meta: {
      skipGlobalError: false,
    },
  });
}
